import { DeviceBox } from "../Components/DeviceBox";
import { Server } from "../Services/Server";
import { Page } from "./Page";
import { StatusBar } from "../Components/StatusBar";
import { Button, Div, PopupMenu, Snack, Span, ClearableTextInput, CollectionPrinter, UnorderedList } from "@tblabs/truffle";
import { Filter } from "../Models/Filter";
import { FilterSelect } from "../Components/FilterSelect";
import { Logger } from "../Components/Logger";
import { LogoutButton } from "../Components/LogoutButton";
import { AuthService } from "../Services/AuthService";
import { AllWatchersWindow } from "../Windows/AllWatchersWindow";
import { DevicesManager } from "../Services/DevicesManager";
import { Device } from "../Services/Device";
import { DeviceSettingsValidationResult } from "../Services/DeviceSettingsValidationResult";


export class PanelPage extends Page
{
    private filterSelector = new FilterSelect(this._devices.GroupFilter).FloatLeft();

    constructor(private _server: Server, private _devices: DevicesManager, private _auth: AuthService, private _logger: Logger)
    {
        super("TX Admin @ " + _server.ConnectedTo, "PanelPage");

        if (!this._auth.IsLoggedIn)
        {
            this._logger.Log("You are not logged in. Redirecting to login page...");
            window.location.href = "/login";
        }

        this.Render();

        this.Logic();
    }

    private Logic(): void
    {
        this._devices.IdFilter.OnChange(() => this._devices.GroupFilter.value = Filter.All);
        this.filterSelector.OnChange(() => this.Refresh());

        let connectionSnack: Snack;
        this._server.OnConnectionChange.Subscribe((connected: boolean) =>
        {
            if (connected)
                this.Refresh();

            if (connected == false)
            {
                connectionSnack = new Snack("🔴 Connection lost", "Server disconnected. Trying to reconnect...", { autoCloseTimeout: 0, clickAnywhereToClose: true });
            }

            else
            {
                connectionSnack?.Close();
            }
        });

        this._server.OnProblem.Subscribe((problem: string, counter: number) =>
        {
            new Snack(`🟠 Server problem #${counter}:`, problem, { autoCloseTimeout: 0, clickAnywhereToClose: true });
        });

        this._devices.OnNewDevice.Subscribe((device: Device) =>
        {
            new Snack(device.Id, "🟢 Connected", { autoCloseTimeout: 5000, clickAnywhereToClose: true });
        });

        this._devices.OnDeviceLost.Subscribe((tid: string) =>
        {
            new Snack(tid, "🔴 Disconnected", { autoCloseTimeout: 5000, clickAnywhereToClose: true });
        });

        this._devices.OnSettingsValidationResult.Subscribe((tid: string, validationResult: DeviceSettingsValidationResult) =>
        {
            new Snack(
                tid + " " + (validationResult.IsValid ? "settings are valid ✅" : "settings are invalid ❌"),
                new UnorderedList(validationResult.Errors), { autoCloseTimeout: validationResult.IsValid ? 1000 : 0, clickAnywhereToClose: true });
        });

        this._devices.Init();
    }

    private Render(): void
    {
        this.header.Append(
            new LogoutButton(this._server));

        this.body
            .Clear()
            .Append(
                new Div().Append(
                    new Div().Append(
                        this.filterSelector,
                        new ClearableTextInput(this._devices.IdFilter, "Filter by Id").Width(132)
                            .OnEnterKeyPressed(() => this.Refresh()),
                        new Button("🍋 Refresh").Class("uk-button uk-button-primary").Style(`white-space: nowrap;`)
                            .OnClick(() => this.Refresh())
                    ).Style("display: flex; justify-content:left;"),
                    new PopupMenu({
                        "🎮 Demo": () => window.location.hash = "demo",
                        "👀 Watchers": () => new AllWatchersWindow(this._server),
                    }, new Button("⫶").Class("uk-button uk-button-primary").PaddingLeft(16).PaddingRight(16))
                ).Style("display: flex; justify-content: space-between; align-items: center;"),

                new Div().Text(this._devices.Status).Margin(48)
                    .VisibleWhen(this._devices.Devices.CountRef, count => count == 0),

                new CollectionPrinter(this._devices.Devices, device => new DeviceBox(device))
                    .Style("display: flex; flex-wrap: wrap; gap: 16px; justify-content: center; margin-top: 32px;"),

                new Span(this._server.Id).Class("server-id")
            );

        this.footer.Append(
            new StatusBar(this._server));
    }

    private Refresh(): void
    {
        this._devices.GetDevices();
    }

    async OnRemove()
    {
        this._server.Dispose();
    }
}
